// src/pages/MasterData/CompanyMasterDataPage.jsx
// =============================================================================
// Company Master Data page
// =============================================================================
//
// PURPOSE
// -------
// This page is the first real editable screen in the new top-level Master Data
// module. It connects the frontend to backend batch 48 and persists the company
// data that will later be used by invoice/receipt PDF generation.
//
// WHY THIS PAGE EXISTS BEFORE INVOICE PRINTING
// --------------------------------------------
// A printed sales invoice needs a reliable seller side:
// - legal company name,
// - tax id / NIP,
// - seller address,
// - contact data,
// - bank account,
// - invoice/footer texts,
// - basic branding / print defaults.
//
// Without this master data, invoice printing would either hardcode company data
// in PDF templates or scatter it across frontend constants. Both approaches are
// fragile and would make future changes expensive.
//
// BACKEND CONTRACT
// ----------------
// This page is aligned with backend routes introduced in backend batch 48:
//
//   GET    /master-data/company
//   PUT    /master-data/company
//   POST   /master-data/company/addresses
//   PUT    /master-data/company/addresses/:id
//   DELETE /master-data/company/addresses/:id
//   POST   /master-data/company/bank-accounts
//   PUT    /master-data/company/bank-accounts/:id
//   DELETE /master-data/company/bank-accounts/:id
//
// Read access is allowed for authenticated internal roles. Write access is
// backend-restricted to superuser. The page mirrors that by disabling edit
// controls for non-superusers, but the backend remains the real security layer.
//
// IMPORTANT SNAPSHOT RULE
// -----------------------
// Company Master Data stores CURRENT seller data. Future posted invoices should
// not read this table live every time a PDF is opened. Instead, sales posting
// should copy a seller snapshot from backend companyMasterDataService into the
// sales document. Old invoice PDFs must not change when the company later edits
// address, bank account or branding.
//
// i18n NOTE
// ---------
// All visible labels/messages come from masterData.json or common.json. English
// is the source/default language, Polish mirrors the same key structure.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import {
  COMPANY_ADDRESS_TYPES,
  KSEF_ENVIRONMENTS,
  VAT_PAYER_STATUSES,
  createCompanyAddress,
  createCompanyBankAccount,
  deleteCompanyAddress,
  deleteCompanyBankAccount,
  fetchCompanyMasterData,
  updateCompanyAddress,
  updateCompanyBankAccount,
  updateCompanyMasterData,
} from "../../services/companyMasterDataService";

import "./MasterDataPage.css";

/* -------------------------------------------------------------------------- */
/* Stable tab definitions                                                     */
/* -------------------------------------------------------------------------- */

const COMPANY_TABS = [
  "general",
  "addresses",
  "contact",
  "bankAccounts",
  "printBranding",
  "integrationsFuture",
];

const PROFILE_TABS = new Set([
  "general",
  "contact",
  "printBranding",
  "integrationsFuture",
]);

/* -------------------------------------------------------------------------- */
/* Defaults aligned with backend model defaults                               */
/* -------------------------------------------------------------------------- */

const EMPTY_COMPANY_PROFILE = Object.freeze({
  legal_name: "",
  trade_name: "",
  tax_id: "",
  regon: "",
  krs: "",
  country_code: "PL",
  default_currency: "PLN",
  default_language: "pl",
  vat_payer_status: VAT_PAYER_STATUSES.UNKNOWN,

  public_email: "",
  invoice_email: "",
  phone: "",
  website: "",


  logo_media_file_id: "",
  small_logo_media_file_id: "",
  signature_media_file_id: "",
  print_accent_style: "default",
  invoice_header_text: "",

  ksef_enabled: false,
  ksef_environment: "",
  fiscal_printer_enabled: false,
  external_accounting_system_id: "",
  integration_notes: "",
});

const EMPTY_ADDRESS = Object.freeze({
  address_type: COMPANY_ADDRESS_TYPES.REGISTERED,
  label: "",
  recipient_name: "",
  address_line1: "",
  address_line2: "",
  postal_code: "",
  city: "",
  region: "",
  country_code: "PL",
  is_default: false,
  is_active: true,
  sort_order: 0,
  comment: "",
});

const EMPTY_BANK_ACCOUNT = Object.freeze({
  account_label: "",
  bank_name: "",
  account_number: "",
  iban: "",
  swift_bic: "",
  currency: "PLN",
  is_default: false,
  is_active: true,
  sort_order: 0,
  comment: "",
});

/* -------------------------------------------------------------------------- */
/* Form field metadata                                                        */
/* -------------------------------------------------------------------------- */

const COMPANY_PROFILE_FIELDS = {
  general: [
    { name: "legal_name", type: "text", required: true },
    { name: "trade_name", type: "text" },
    { name: "tax_id", type: "text" },
    { name: "regon", type: "text" },
    { name: "krs", type: "text" },
    { name: "country_code", type: "text", maxLength: 2 },
    { name: "default_currency", type: "text", maxLength: 3 },
    {
      name: "default_language",
      type: "select",
      options: ["pl", "en"],
    },
    {
      name: "vat_payer_status",
      type: "select",
      options: Object.values(VAT_PAYER_STATUSES),
    },
  ],
  contact: [
    { name: "public_email", type: "email" },
    { name: "invoice_email", type: "email" },
    { name: "phone", type: "text" },
    { name: "website", type: "url" },
  ],
  printBranding: [
    { name: "logo_media_file_id", type: "number", min: 0 },
    { name: "small_logo_media_file_id", type: "number", min: 0 },
    { name: "signature_media_file_id", type: "number", min: 0 },
    {
      name: "print_accent_style",
      type: "select",
      options: ["default", "classic", "elegant", "minimal"],
    },
    { name: "invoice_header_text", type: "textarea", wide: true },
  ],
  integrationsFuture: [
    { name: "ksef_enabled", type: "checkbox" },
    {
      name: "ksef_environment",
      type: "select",
      options: ["", ...Object.values(KSEF_ENVIRONMENTS)],
    },
    { name: "fiscal_printer_enabled", type: "checkbox" },
    { name: "external_accounting_system_id", type: "text" },
    { name: "integration_notes", type: "textarea", wide: true },
  ],
};

const ADDRESS_FIELDS = [
  {
    name: "address_type",
    type: "select",
    options: Object.values(COMPANY_ADDRESS_TYPES),
  },
  { name: "label", type: "text" },
  { name: "recipient_name", type: "text" },
  { name: "address_line1", type: "text" },
  { name: "address_line2", type: "text" },
  { name: "postal_code", type: "text" },
  { name: "city", type: "text" },
  { name: "region", type: "text" },
  { name: "country_code", type: "text", maxLength: 2 },
  { name: "sort_order", type: "number", min: 0 },
  { name: "is_default", type: "checkbox" },
  { name: "comment", type: "textarea", wide: true },
];

const BANK_ACCOUNT_FIELDS = [
  { name: "account_label", type: "text" },
  { name: "bank_name", type: "text" },
  { name: "account_number", type: "text" },
  { name: "iban", type: "text" },
  { name: "swift_bic", type: "text" },
  { name: "currency", type: "text", maxLength: 3 },
  { name: "sort_order", type: "number", min: 0 },
  { name: "is_default", type: "checkbox" },
  { name: "comment", type: "textarea", wide: true },
];

const CONSUMER_KEYS = [
  "invoicePdf",
  "salesDocuments",
  "voucherPackagePdf",
  "publicShop",
  "emails",
  "futureIntegrations",
];

/* -------------------------------------------------------------------------- */
/* Contextual guidance definitions                                             */
/* -------------------------------------------------------------------------- */

// The Company Master Data module contains fields that are business-critical but
// not always self-explanatory for salon users. These stable keys drive the
// visible help cards below. Keeping the content in i18n instead of hardcoding it
// here preserves the bilingual pattern used across the project.
const COMPANY_TAB_GUIDANCE_KEYS = {
  general: ["legalIdentity", "tradeIdentity", "taxIdentity", "defaults"],
  contact: ["publicContact", "invoiceContact", "documentReuse"],
  printBranding: ["mediaReferences", "printStyles", "futurePdfReuse"],
  integrationsFuture: ["ksef", "fiscalPrinter", "accountingExport"],
};

const ADDRESS_TYPE_GUIDANCE_KEYS = [
  "registered",
  "operating",
  "correspondence",
  "invoicing",
  "other",
];

const BANK_ACCOUNT_GUIDANCE_KEYS = [
  "defaultAccount",
  "ibanVsAccountNumber",
  "snapshotRule",
];

/* -------------------------------------------------------------------------- */
/* Normalization helpers                                                      */
/* -------------------------------------------------------------------------- */

function valueOrEmpty(value) {
  return value === undefined || value === null ? "" : value;
}

function normalizeCompanyProfile(company = {}) {
  return {
    ...EMPTY_COMPANY_PROFILE,
    ...company,
    logo_media_file_id: valueOrEmpty(company.logo_media_file_id),
    small_logo_media_file_id: valueOrEmpty(company.small_logo_media_file_id),
    signature_media_file_id: valueOrEmpty(company.signature_media_file_id),
    ksef_enabled: company.ksef_enabled === true,
    fiscal_printer_enabled: company.fiscal_printer_enabled === true,
  };
}

function normalizeAddress(row = {}) {
  return {
    ...EMPTY_ADDRESS,
    ...row,
    sort_order: valueOrEmpty(row.sort_order) || 0,
    is_default: row.is_default === true,
    is_active: row.is_active !== false,
  };
}

function normalizeBankAccount(row = {}) {
  return {
    ...EMPTY_BANK_ACCOUNT,
    ...row,
    sort_order: valueOrEmpty(row.sort_order) || 0,
    is_default: row.is_default === true,
    is_active: row.is_active !== false,
  };
}

function sortRows(a, b) {
  return Number(a?.sort_order || 0) - Number(b?.sort_order || 0) || Number(a?.id || 0) - Number(b?.id || 0);
}

/* -------------------------------------------------------------------------- */
/* Small rendering helpers                                                    */
/* -------------------------------------------------------------------------- */

function formatAddressLine(row = {}) {
  const parts = [
    row.address_line1,
    row.address_line2,
    [row.postal_code, row.city].filter(Boolean).join(" "),
    row.region,
    row.country_code,
  ].filter(Boolean);

  return parts.join(", ");
}

function optionLabel(t, namespaceKey, value) {
  return t(`${namespaceKey}.${value || "empty"}`, { defaultValue: value || "—" });
}

function addressTypeLabel(t, addressType) {
  return optionLabel(t, "masterData:company.form.address.options.address_type", addressType);
}

function buildAddressCardTitle(row = {}, t) {
  // Saved addresses need a readable title even when the optional label field is
  // empty. The fallback sequence intentionally prefers business-facing values
  // before falling back to the technical address type label. This makes the
  // right-side list useful as a navigation area, not only as a raw record dump.
  return (
    row.label ||
    row.recipient_name ||
    row.address_line1 ||
    row.city ||
    addressTypeLabel(t, row.address_type)
  );
}

function MessageBanner({ type, children }) {
  if (!children) return null;

  return (
    <div className={`master-data-message master-data-message--${type}`}>
      {children}
    </div>
  );
}

function GuidanceGrid({ title, description, items, t, translationPrefix }) {
  if (!items?.length) return null;

  return (
    <section className="master-data-guidance" aria-label={title}>
      <div className="master-data-guidance__header">
        <strong>{title}</strong>
        {description ? <p>{description}</p> : null}
      </div>

      <div className="master-data-guidance__grid">
        {items.map((itemKey) => (
          <article key={itemKey} className="master-data-guidance__item">
            <h4>{t(`${translationPrefix}.${itemKey}.title`)}</h4>
            <p>{t(`${translationPrefix}.${itemKey}.description`)}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function FormField({ field, value, disabled, t, onChange, translationPrefix }) {
  const inputId = `${translationPrefix}-${field.name}`;
  const label = t(`${translationPrefix}.fields.${field.name}.label`);
  const hint = t(`${translationPrefix}.fields.${field.name}.hint`, { defaultValue: "" });
  const fieldClass = `master-data-form-field ${field.wide ? "master-data-form-field--wide" : ""}`;

  if (field.type === "checkbox") {
    return (
      <label className={`${fieldClass} master-data-check-field`} htmlFor={inputId}>
        <input
          id={inputId}
          type="checkbox"
          checked={value === true}
          disabled={disabled}
          onChange={(event) => onChange(field.name, event.target.checked)}
        />
        <span>
          <strong>{label}</strong>
          {hint ? <small>{hint}</small> : null}
        </span>
      </label>
    );
  }

  return (
    <label className={fieldClass} htmlFor={inputId}>
      <span>{label}</span>
      {field.type === "select" ? (
        <select
          id={inputId}
          value={value ?? ""}
          disabled={disabled}
          onChange={(event) => onChange(field.name, event.target.value)}
        >
          {(field.options || []).map((option) => (
            <option key={option || "empty"} value={option}>
              {optionLabel(t, `${translationPrefix}.options.${field.name}`, option)}
            </option>
          ))}
        </select>
      ) : field.type === "textarea" ? (
        <textarea
          id={inputId}
          value={value ?? ""}
          disabled={disabled}
          rows={4}
          onChange={(event) => onChange(field.name, event.target.value)}
        />
      ) : (
        <input
          id={inputId}
          type={field.type || "text"}
          value={value ?? ""}
          min={field.min}
          max={field.max}
          maxLength={field.maxLength}
          disabled={disabled}
          onChange={(event) => onChange(field.name, event.target.value)}
        />
      )}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Main page                                                                  */
/* -------------------------------------------------------------------------- */

function CompanyMasterDataPage() {
  const { t } = useTranslation(["masterData", "navigation", "common"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "user";
  const canWrite = currentRole === "superuser";

  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [companyForm, setCompanyForm] = useState(() => ({ ...EMPTY_COMPANY_PROFILE }));
  const [addresses, setAddresses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [addressDraft, setAddressDraft] = useState(() => ({ ...EMPTY_ADDRESS }));
  const [editingAddressId, setEditingAddressId] = useState(null);

  const [bankDraft, setBankDraft] = useState(() => ({ ...EMPTY_BANK_ACCOUNT }));
  const [editingBankAccountId, setEditingBankAccountId] = useState(null);

  const activeProfileFields = useMemo(() => {
    return COMPANY_PROFILE_FIELDS[activeTab] || [];
  }, [activeTab]);

  const sortedAddresses = useMemo(() => {
    return [...addresses].sort(sortRows);
  }, [addresses]);

  const sortedBankAccounts = useMemo(() => {
    return [...bankAccounts].sort(sortRows);
  }, [bankAccounts]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const aggregate = await fetchCompanyMasterData();
      setCompanyForm(normalizeCompanyProfile(aggregate.company));
      setAddresses((aggregate.addresses || []).map(normalizeAddress));
      setBankAccounts((aggregate.bankAccounts || []).map(normalizeBankAccount));
    } catch (err) {
      setError(err?.message || t("masterData:company.messages.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clearMessages = useCallback(() => {
    setError("");
    setSuccess("");
  }, []);

  const updateCompanyField = useCallback((name, value) => {
    clearMessages();
    setCompanyForm((prev) => ({ ...prev, [name]: value }));
  }, [clearMessages]);

  const updateAddressField = useCallback((name, value) => {
    clearMessages();
    setAddressDraft((prev) => ({ ...prev, [name]: value }));
  }, [clearMessages]);

  const updateBankField = useCallback((name, value) => {
    clearMessages();
    setBankDraft((prev) => ({ ...prev, [name]: value }));
  }, [clearMessages]);

  const saveCompanyProfile = useCallback(async () => {
    if (!canWrite) return;

    setSaving(true);
    clearMessages();

    try {
      const aggregate = await updateCompanyMasterData(companyForm);
      setCompanyForm(normalizeCompanyProfile(aggregate.company));
      setAddresses((aggregate.addresses || []).map(normalizeAddress));
      setBankAccounts((aggregate.bankAccounts || []).map(normalizeBankAccount));
      setSuccess(t("masterData:company.messages.profileSaved"));
    } catch (err) {
      setError(err?.message || t("masterData:company.messages.saveError"));
    } finally {
      setSaving(false);
    }
  }, [canWrite, clearMessages, companyForm, t]);

  const resetAddressDraft = useCallback(() => {
    setEditingAddressId(null);
    setAddressDraft({ ...EMPTY_ADDRESS });
  }, []);

  const startEditAddress = useCallback((row) => {
    clearMessages();
    setEditingAddressId(row.id);
    setAddressDraft(normalizeAddress(row));
    setActiveTab("addresses");
  }, [clearMessages]);

  const saveAddress = useCallback(async () => {
    if (!canWrite) return;

    setSaving(true);
    clearMessages();

    try {
      if (editingAddressId) {
        await updateCompanyAddress(editingAddressId, addressDraft);
      } else {
        await createCompanyAddress(addressDraft);
      }

      resetAddressDraft();
      await loadData();
      setSuccess(t("masterData:company.messages.addressSaved"));
    } catch (err) {
      setError(err?.message || t("masterData:company.messages.saveError"));
    } finally {
      setSaving(false);
    }
  }, [addressDraft, canWrite, clearMessages, editingAddressId, loadData, resetAddressDraft, t]);

  const removeAddress = useCallback(async (row) => {
    if (!canWrite || !row?.id) return;

    const confirmed = window.confirm(t("masterData:company.messages.confirmDeleteAddress"));
    if (!confirmed) return;

    setSaving(true);
    clearMessages();

    try {
      await deleteCompanyAddress(row.id);
      if (String(editingAddressId || "") === String(row.id)) {
        resetAddressDraft();
      }
      await loadData();
      setSuccess(t("masterData:company.messages.addressDeleted"));
    } catch (err) {
      setError(err?.message || t("masterData:company.messages.deleteError"));
    } finally {
      setSaving(false);
    }
  }, [canWrite, clearMessages, editingAddressId, loadData, resetAddressDraft, t]);

  const resetBankDraft = useCallback(() => {
    setEditingBankAccountId(null);
    setBankDraft({ ...EMPTY_BANK_ACCOUNT });
  }, []);

  const startEditBankAccount = useCallback((row) => {
    clearMessages();
    setEditingBankAccountId(row.id);
    setBankDraft(normalizeBankAccount(row));
    setActiveTab("bankAccounts");
  }, [clearMessages]);

  const saveBankAccount = useCallback(async () => {
    if (!canWrite) return;

    setSaving(true);
    clearMessages();

    try {
      if (editingBankAccountId) {
        await updateCompanyBankAccount(editingBankAccountId, bankDraft);
      } else {
        await createCompanyBankAccount(bankDraft);
      }

      resetBankDraft();
      await loadData();
      setSuccess(t("masterData:company.messages.bankAccountSaved"));
    } catch (err) {
      setError(err?.message || t("masterData:company.messages.saveError"));
    } finally {
      setSaving(false);
    }
  }, [bankDraft, canWrite, clearMessages, editingBankAccountId, loadData, resetBankDraft, t]);

  const removeBankAccount = useCallback(async (row) => {
    if (!canWrite || !row?.id) return;

    const confirmed = window.confirm(t("masterData:company.messages.confirmDeleteBankAccount"));
    if (!confirmed) return;

    setSaving(true);
    clearMessages();

    try {
      await deleteCompanyBankAccount(row.id);
      if (String(editingBankAccountId || "") === String(row.id)) {
        resetBankDraft();
      }
      await loadData();
      setSuccess(t("masterData:company.messages.bankAccountDeleted"));
    } catch (err) {
      setError(err?.message || t("masterData:company.messages.deleteError"));
    } finally {
      setSaving(false);
    }
  }, [canWrite, clearMessages, editingBankAccountId, loadData, resetBankDraft, t]);

  const renderProfileTab = () => (
    <>
      <div className="master-data-tab-panel__intro">
        <h3>{t(`masterData:company.tabs.${activeTab}.title`)}</h3>
        <p>{t(`masterData:company.tabs.${activeTab}.description`)}</p>
      </div>

      <GuidanceGrid
        title={t(`masterData:company.guidance.tabs.${activeTab}.title`)}
        description={t(`masterData:company.guidance.tabs.${activeTab}.description`, { defaultValue: "" })}
        items={COMPANY_TAB_GUIDANCE_KEYS[activeTab] || []}
        t={t}
        translationPrefix={`masterData:company.guidance.tabs.${activeTab}.items`}
      />

      <div className="master-data-form-grid">
        {activeProfileFields.map((field) => (
          <FormField
            key={field.name}
            field={field}
            value={companyForm[field.name]}
            disabled={!canWrite || saving || loading}
            t={t}
            onChange={updateCompanyField}
            translationPrefix="masterData:company.form.profile"
          />
        ))}
      </div>

      <div className="master-data-actions-row">
        <button
          type="button"
          className="master-data-primary-btn"
          disabled={!canWrite || saving || loading}
          onClick={saveCompanyProfile}
        >
          {saving ? t("masterData:company.actions.saving") : t("masterData:company.actions.saveProfile")}
        </button>

        <button
          type="button"
          className="master-data-secondary-btn"
          disabled={saving || loading}
          onClick={loadData}
        >
          {t("common:actions.refresh")}
        </button>
      </div>
    </>
  );

  const renderAddressesTab = () => (
    <>
      <div className="master-data-tab-panel__intro">
        <h3>{t("masterData:company.tabs.addresses.title")}</h3>
        <p>{t("masterData:company.tabs.addresses.description")}</p>
      </div>

      <GuidanceGrid
        title={t("masterData:company.guidance.addressTypes.title")}
        description={t("masterData:company.guidance.addressTypes.description")}
        items={ADDRESS_TYPE_GUIDANCE_KEYS}
        t={t}
        translationPrefix="masterData:company.guidance.addressTypes.items"
      />

      <div className="master-data-two-column-layout">
        <section className="master-data-subcard">
          <div className="master-data-subcard__header">
            <h4>
              {editingAddressId
                ? t("masterData:company.addresses.editTitle")
                : t("masterData:company.addresses.createTitle")}
            </h4>
            {editingAddressId ? (
              <button type="button" className="master-data-text-btn" onClick={resetAddressDraft} disabled={saving}>
                {t("common:actions.cancel")}
              </button>
            ) : null}
          </div>

          <div className="master-data-form-grid master-data-form-grid--compact">
            {ADDRESS_FIELDS.map((field) => (
              <FormField
                key={field.name}
                field={field}
                value={addressDraft[field.name]}
                disabled={!canWrite || saving || loading}
                t={t}
                onChange={updateAddressField}
                translationPrefix="masterData:company.form.address"
              />
            ))}
          </div>

          <div className="master-data-actions-row">
            <button
              type="button"
              className="master-data-primary-btn"
              disabled={!canWrite || saving || loading}
              onClick={saveAddress}
            >
              {saving ? t("masterData:company.actions.saving") : t("masterData:company.actions.saveAddress")}
            </button>
          </div>
        </section>

        <section className="master-data-subcard">
          <div className="master-data-subcard__header">
            <h4>{t("masterData:company.addresses.listTitle")}</h4>
            <span className="master-data-counter-chip">{sortedAddresses.length}</span>
          </div>

          {sortedAddresses.length === 0 ? (
            <p className="master-data-empty-state">{t("masterData:company.addresses.empty")}</p>
          ) : (
            <>
              <p className="master-data-list-help">{t("masterData:company.addresses.navigationHelp")}</p>

              <div className="master-data-record-list">
                {sortedAddresses.map((row) => {
                  const selected = String(editingAddressId || "") === String(row.id);
                  const addressLine = formatAddressLine(row);

                  return (
                    <article
                      key={row.id}
                      className={`master-data-record-card master-data-record-card--clickable ${selected ? "master-data-record-card--selected" : ""}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      onClick={() => startEditAddress(row)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          startEditAddress(row);
                        }
                      }}
                    >
                      <div className="master-data-record-main">
                        <div className="master-data-record-title-row">
                          <span className="master-data-record-type-chip">{addressTypeLabel(t, row.address_type)}</span>
                          <strong>{buildAddressCardTitle(row, t)}</strong>
                          {row.is_default ? <span>{t("masterData:company.badges.default")}</span> : null}
                        </div>

                        <p>{addressLine || t("masterData:company.addresses.noAddressLine")}</p>

                        <small>
                          {row.recipient_name
                            ? t("masterData:company.addresses.recipientMeta", { recipient: row.recipient_name })
                            : t("masterData:company.addresses.typeMeta", { type: addressTypeLabel(t, row.address_type) })}
                        </small>
                      </div>

                      <div className="master-data-record-actions">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            startEditAddress(row);
                          }}
                          disabled={saving}
                        >
                          {canWrite ? t("common:actions.edit") : t("masterData:company.actions.view")}
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            removeAddress(row);
                          }}
                          disabled={!canWrite || saving}
                        >
                          {t("common:actions.delete")}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );

  const renderBankAccountsTab = () => (
    <>
      <div className="master-data-tab-panel__intro">
        <h3>{t("masterData:company.tabs.bankAccounts.title")}</h3>
        <p>{t("masterData:company.tabs.bankAccounts.description")}</p>
      </div>

      <GuidanceGrid
        title={t("masterData:company.guidance.bankAccounts.title")}
        description={t("masterData:company.guidance.bankAccounts.description")}
        items={BANK_ACCOUNT_GUIDANCE_KEYS}
        t={t}
        translationPrefix="masterData:company.guidance.bankAccounts.items"
      />

      <div className="master-data-two-column-layout">
        <section className="master-data-subcard">
          <div className="master-data-subcard__header">
            <h4>
              {editingBankAccountId
                ? t("masterData:company.bankAccounts.editTitle")
                : t("masterData:company.bankAccounts.createTitle")}
            </h4>
            {editingBankAccountId ? (
              <button type="button" className="master-data-text-btn" onClick={resetBankDraft} disabled={saving}>
                {t("common:actions.cancel")}
              </button>
            ) : null}
          </div>

          <div className="master-data-form-grid master-data-form-grid--compact">
            {BANK_ACCOUNT_FIELDS.map((field) => (
              <FormField
                key={field.name}
                field={field}
                value={bankDraft[field.name]}
                disabled={!canWrite || saving || loading}
                t={t}
                onChange={updateBankField}
                translationPrefix="masterData:company.form.bankAccount"
              />
            ))}
          </div>

          <div className="master-data-actions-row">
            <button
              type="button"
              className="master-data-primary-btn"
              disabled={!canWrite || saving || loading}
              onClick={saveBankAccount}
            >
              {saving ? t("masterData:company.actions.saving") : t("masterData:company.actions.saveBankAccount")}
            </button>
          </div>
        </section>

        <section className="master-data-subcard">
          <div className="master-data-subcard__header">
            <h4>{t("masterData:company.bankAccounts.listTitle")}</h4>
            <span className="master-data-counter-chip">{sortedBankAccounts.length}</span>
          </div>

          {sortedBankAccounts.length === 0 ? (
            <p className="master-data-empty-state">{t("masterData:company.bankAccounts.empty")}</p>
          ) : (
            <div className="master-data-record-list">
              {sortedBankAccounts.map((row) => (
                <article key={row.id} className="master-data-record-card">
                  <div>
                    <div className="master-data-record-title-row">
                      <strong>{row.account_label || row.bank_name || t("masterData:company.bankAccounts.unnamed")}</strong>
                      {row.is_default ? <span>{t("masterData:company.badges.default")}</span> : null}
                    </div>
                    <p>{row.iban || row.account_number || t("masterData:company.bankAccounts.noAccountNumber")}</p>
                    <small>{[row.bank_name, row.swift_bic, row.currency].filter(Boolean).join(" · ")}</small>
                  </div>

                  <div className="master-data-record-actions">
                    <button type="button" onClick={() => startEditBankAccount(row)} disabled={!canWrite || saving}>
                      {t("common:actions.edit")}
                    </button>
                    <button type="button" onClick={() => removeBankAccount(row)} disabled={!canWrite || saving}>
                      {t("common:actions.delete")}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );

  const renderActiveTab = () => {
    if (activeTab === "addresses") return renderAddressesTab();
    if (activeTab === "bankAccounts") return renderBankAccountsTab();
    if (PROFILE_TABS.has(activeTab)) return renderProfileTab();
    return null;
  };

  return (
    <div className="master-data-page">
      <div className="master-data-page__inner">
        <header className="master-data-header">
          <div>
            <p className="master-data-eyebrow">{t("navigation:masterData")}</p>
            <h1>{t("masterData:company.title")}</h1>
            <p className="master-data-subtitle">{t("masterData:company.subtitle")}</p>
          </div>

          <div className="master-data-header-actions">
            <span className="master-data-status-chip master-data-status-chip--success">
              {t("masterData:common.backendConnected")}
            </span>
            {!canWrite ? (
              <span className="master-data-status-chip master-data-status-chip--muted">
                {t("masterData:common.readOnly")}
              </span>
            ) : null}
          </div>
        </header>

        <MessageBanner type="error">{error}</MessageBanner>
        <MessageBanner type="success">{success}</MessageBanner>

        {!canWrite ? (
          <section className="master-data-warning-card master-data-warning-card--compact">
            <strong>{t("masterData:company.readOnly.title")}</strong>
            <p>{t("masterData:company.readOnly.description")}</p>
          </section>
        ) : null}

        <section className="master-data-info-grid" aria-label={t("masterData:company.overviewAriaLabel")}>
          <article className="master-data-info-card master-data-info-card--accent">
            <h2>{t("masterData:company.invoiceDependency.title")}</h2>
            <p>{t("masterData:company.invoiceDependency.description")}</p>
          </article>

          <article className="master-data-info-card">
            <h2>{t("masterData:company.snapshotRule.title")}</h2>
            <p>{t("masterData:company.snapshotRule.description")}</p>
          </article>
        </section>

        <section className="master-data-card">
          <div className="master-data-card__header">
            <div>
              <h2>{t("masterData:company.structureTitle")}</h2>
              <p>{t("masterData:company.structureDescription")}</p>
            </div>
          </div>

          <div className="master-data-tabs" role="tablist" aria-label={t("masterData:company.tabsAriaLabel")}>
            {COMPANY_TABS.map((tabKey) => (
              <button
                key={tabKey}
                type="button"
                role="tab"
                className={`master-data-tab ${activeTab === tabKey ? "master-data-tab--active" : ""}`}
                aria-selected={activeTab === tabKey}
                onClick={() => setActiveTab(tabKey)}
              >
                {t(`masterData:company.tabs.${tabKey}.label`)}
              </button>
            ))}
          </div>

          <div className="master-data-tab-panel" role="tabpanel" aria-busy={loading}>
            {loading ? (
              <div className="master-data-loading-state">{t("common:common.loading")}</div>
            ) : (
              renderActiveTab()
            )}
          </div>
        </section>

        <section className="master-data-card master-data-card--consumers">
          <div className="master-data-card__header">
            <div>
              <h2>{t("masterData:company.consumers.title")}</h2>
              <p>{t("masterData:company.consumers.description")}</p>
            </div>
          </div>

          <div className="master-data-consumer-grid">
            {CONSUMER_KEYS.map((consumerKey) => (
              <article key={consumerKey} className="master-data-consumer-card">
                <h3>{t(`masterData:company.consumers.items.${consumerKey}.title`)}</h3>
                <p>{t(`masterData:company.consumers.items.${consumerKey}.description`)}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="master-data-warning-card">
          <strong>{t("masterData:company.nextStep.title")}</strong>
          <p>{t("masterData:company.nextStep.description")}</p>
        </section>
      </div>
    </div>
  );
}

export default CompanyMasterDataPage;
